import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ApiService } from '@app/core/services/api/api.service';
import { ShopService } from '@app/services/shops/shop.service';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { OrderEditing } from '@pdqdev/pdq-shared/dist/interfaces';
import { Observable, BehaviorSubject, tap, filter, take, map, switchMap, finalize, of, catchError } from 'rxjs';
import {
  AddressCorrectionCountResponse,
  AddressCorrectionHistoryQuery,
  AddressCorrectionHistoryResponse,
} from './components/order-editing-form/components/order-editing-form-address-validation/components/address-validation-flagged-orders/address-validation-flagged-orders.types';
import {
  OrderEditingConfigForm,
  getOrderEditingMetricsConfig,
  OrderEditMetricsDto,
  OrderEditingMetric,
  DEFAULT_DISCOUNT_REMOVED_MESSAGE,
} from './order-editing.types';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { RestrictionRules } from './order-editing-rules.config';
import { environment } from '@environment/environment';
import { Shop } from '@app/services/shops/shop.model';
import { abbreviateLargeNumber } from '@utils/string-utils';
import { DecimalPipe } from '@angular/common';
import { getSignOfCurrency } from '@utils/localization-utils';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '@app/sharedModule/components/confirmation-dialog/confirmation-dialog.component';
import { OrderEditingFormTab } from './components/order-editing-form/order-editing-form.component';

@Injectable({
  providedIn: 'root',
})
export class OrderEditingService {
  form: FormGroup<OrderEditingConfigForm>;
  private _loadedConfig$ = new BehaviorSubject<OrderEditing.Config>(null);
  private static readonly isNewMetrics = !!environment.ENABLE_ORDER_EDITING_NEW_METRICS;
  private _metrics$ = new BehaviorSubject<Record<string, OrderEditingMetric>>(
    getOrderEditingMetricsConfig(OrderEditingService.isNewMetrics),
  );
  private static readonly API_URL = environment.ORDER_EDITING_API_URL ?? 'http://localhost:3022';
  private readonly _onSetTab$ = new EventEmitter<OrderEditingFormTab>(null);

  static readonly requiredScopes = ['write_order_edits', 'read_order_edits'];
  updateRequired = signal(false);
  updateAppUrl = signal('');
  metricsLoading = signal(false);
  hidePreview = signal(false);
  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private toastService: ToastService,
    private rulesDynamicFormService: RulesDynamicFormService,
    private shopService: ShopService,
    private decimalPipe: DecimalPipe,
    private matDialog: MatDialog,
  ) {
    this.initForm();
    this.subscribeToConfig();
    this.loadConfig();
    this.initUpdateRequired();
  }

  get loadedConfig$() {
    return this._loadedConfig$.asObservable();
  }

  get metrics$() {
    return this._metrics$.asObservable();
  }

  initUpdateRequired() {
    this.shopService.getUrlToUpdateAppIfRequired(OrderEditingService.requiredScopes).subscribe((res) => {
      if (!res) {
        return;
      }
      this.updateRequired.set(res.requireUpdate);
      this.updateAppUrl.set(res.updateUrl);
    });
  }

  private patchExistingForm(newValue: Partial<OrderEditing.Config>) {
    const value = this.createForm(newValue).value;
    this.form.patchValue(value, { emitEvent: false });

    if (value.state) {
      this.form.get('state')?.patchValue(value.state, { emitEvent: true });
    }

    const editsForm = this.form.get('template.edits') as FormGroup;

    editsForm.setControl(
      'rules',
      this.rulesDynamicFormService.initRulesFormArray(value?.template?.edits?.rules ?? [], RestrictionRules),
      { emitEvent: false },
    );

    this.form.markAsPristine();
  }

  initForm(config?: OrderEditing.Config) {
    const initialValue =
      config ??
      ({
        state: OrderEditing.State.DISABLED,
      } as Partial<OrderEditing.Config>);

    if (!this?.form?.controls) {
      this.form = this.createForm(initialValue);
    } else if (config) {
      this.patchExistingForm(initialValue);
    }
  }

  get onSetTab$() {
    return this._onSetTab$.asObservable();
  }

  setActiveTab(tab: OrderEditingFormTab) {
    this._onSetTab$.emit(tab);
  }

  createForm(initialValue?: Partial<OrderEditing.Config>) {
    const template = initialValue?.template ?? ({} as Partial<OrderEditing.Config['template']>);
    return new FormGroup<OrderEditingConfigForm>({
      state: new FormControl(initialValue?.state),
      template: new FormGroup({
        edits: this.initEditsConfigForm(template.edits),
        repricing: this.initRepricingConfigForm(template.repricing),
        periods: this.initPeriodsConfigForm(template.periods),
        settings: this.initSettingsConfigForm(template.settings),
        addressValidation: this.initAddressValidationConfigForm(template.addressValidation),
      }),
    });
  }

  private initShippingRecalculationConfigForm(shipping?: OrderEditing.ShippingRecalculationConfig) {
    return new FormGroup({
      enabled: new FormControl(shipping?.enabled ?? false),
      recalculationMode: new FormControl(OrderEditing.ShippingRecalculationMode.AFTER_EVERY_EDIT),
      customerExperience: new FormControl(OrderEditing.ShippingCustomerExperience.CUSTOMER_SELECT),
      allowDowngrades: new FormControl(shipping?.allowDowngrades ?? false),
      downgradeRefundHandlingType: new FormControl(
        shipping?.downgradeRefundHandlingType ?? OrderEditing.RefundHandlingType.MANUAL,
      ),
      eligibleMethodsMode: new FormControl(
        shipping?.eligibleMethodsMode ?? OrderEditing.ShippingEligibleMethodsMode.ALL,
      ),
      methodNames: new FormControl(shipping?.methodNames ?? []),
      minimumChargeThreshold: new FormControl(shipping?.minimumChargeThreshold ?? 0),
    });
  }

  private initSettingsConfigForm(settings?: OrderEditing.Settings) {
    const discountCodesSettings = settings?.['discountCodes'];
    return new FormGroup({
      useShopifyFlows: new FormControl(settings?.useShopifyFlows ?? false),
      fulfillmentHoldReleaseMode: new FormControl(
        settings?.fulfillmentHoldReleaseMode ?? OrderEditing.FulfillmentHoldReleaseMode.ALL_HOLDS,
      ),
      shippingRecalculation: this.initShippingRecalculationConfigForm(settings?.shippingRecalculation),
      discountCodes: new FormGroup({
        recalculateEligibility: new FormControl(discountCodesSettings?.recalculateEligibility ?? true),
        discountRemovedMessage: new FormControl(
          discountCodesSettings?.discountRemovedMessage ?? DEFAULT_DISCOUNT_REMOVED_MESSAGE,
        ),
      }),
    });
  }

  resetForm() {
    const loadedConfig = this._loadedConfig$.getValue();
    if (loadedConfig) {
      // Use the same approach as patchExistingForm to properly restore FormArrays
      this.patchExistingForm(loadedConfig);
      this.form.markAsPristine();
    }
  }

  private initEditsConfigForm(edits?: OrderEditing.EditsConfig) {
    return new FormGroup({
      isDisplayed: new FormControl(edits?.isDisplayed ?? false),
      product: new FormGroup({
        variant: new FormControl(edits?.product?.variant ?? true),
        quantity: new FormControl(edits?.product?.quantity ?? true),
        addProducts: new FormControl(edits?.product?.addProducts ?? edits?.product?.quantity ?? true),
        orderNote: new FormControl(edits?.product?.orderNote ?? true),
        cancelOrder: new FormControl(edits?.product?.cancelOrder ?? false),
        changeFulfillmentLocation: new FormControl(edits?.product?.changeFulfillmentLocation ?? false),
        outOfStockProducts: new FormControl(edits?.product?.outOfStockProducts ?? false),
        discountCode: new FormControl(edits?.product?.discountCode ?? false),
      }),
      customerDetails: new FormGroup({
        allowChangingDestinationCountry: new FormControl(
          edits?.customerDetails?.allowChangingDestinationCountry ?? false,
        ),
        allowEditPhone: new FormControl(
          edits?.customerDetails?.allowEditPhone ?? !edits?.customerDetails?.allowChangingDestinationCountry,
        ),
        allowEditEmail: new FormControl(
          edits?.customerDetails?.allowEditEmail ?? !edits?.customerDetails?.allowChangingDestinationCountry,
        ),
        shippingAddress: new FormControl(
          edits?.customerDetails?.shippingAddress ?? !edits?.customerDetails?.allowChangingDestinationCountry,
        ),
        allowTaxRecalculation: new FormControl(edits?.customerDetails?.allowTaxRecalculation ?? true),
      }),
      rulesEnabled: new FormControl(edits?.rulesEnabled ?? true),
      rules: this.rulesDynamicFormService.initRulesFormArray(edits?.rules ?? [], RestrictionRules),
    });
  }

  private initAddressValidationConfigForm(addressValidation?: OrderEditing.AddressValidationConfig) {
    return new FormGroup({
      enabled: new FormControl(addressValidation?.enabled ?? false),
    });
  }

  get useShopifyFlows(): boolean {
    return this.form.get('template.settings.useShopifyFlows')?.value;
  }

  private initRepricingConfigForm(repricing?: OrderEditing.RepricingConfig) {
    return new FormGroup({
      itemRemovalAdjustment: new FormControl(repricing?.itemRemovalAdjustment ?? false),
      itemRemovalRefundHandlingType: new FormControl(
        repricing?.itemRemovalRefundHandlingType ?? OrderEditing.RefundHandlingType.MANUAL,
      ),
      unpaidChangesHandlingType: new FormControl(
        repricing?.unpaidChangesHandlingType ?? OrderEditing.UnpaidChangesHandlingType.CONTINUE_PARTIALLY_PAID,
      ),
      taxAdjustments: new FormGroup({
        enabled: new FormControl(repricing?.taxAdjustments?.enabled ?? false),
        type: new FormControl(repricing?.taxAdjustments?.type ?? OrderEditing.TaxAdjustmentType.RECALCULATE),
      }),
    });
  }

  private initPeriodsConfigForm(periods?: OrderEditing.PeriodConfig) {
    return new FormGroup(
      {
        type: new FormControl(periods?.type ?? OrderEditing.PeriodType.MINUTES),
        value: new FormControl(periods?.value ?? 30),
        extendOnNonBusinessDays: new FormControl(periods?.extendOnNonBusinessDays ?? false),
      },
      { validators: [this.validatePeriodValue] },
    );
  }

  private validatePeriodValue(form: FormGroup) {
    const value = +form.get('value').value;
    const type = form.get('type').value;

    switch (type) {
      case OrderEditing.PeriodType.MINUTES:
        if (value < 5 || value > 59) {
          return { invalid: true };
        }
        break;
      case OrderEditing.PeriodType.HOURS:
        if (value < 1 || value > 24) {
          return { invalid: true };
        }
        break;
    }
    return null;
  }

  private subscribeToConfig() {
    this.loadedConfig$.subscribe((config) => {
      this.initForm(config);
    });
  }

  public loadMetrics(startDate: string, endDate: string) {
    const params = { startDate, endDate };
    this.metricsLoading.set(true);
    this.http
      .get<OrderEditMetricsDto>(`${OrderEditingService.API_URL}/order-edits/metrics`, {
        headers: {
          Authorization: `Bearer ${this.apiService.jwtToken}`,
        },
        params: params,
      })
      .pipe(
        switchMap((metrics) =>
          this.shopService.shop$.pipe(
            filter((shop) => !!shop),
            take(1),
            map((shop) => this.parseLoadedMetrics(shop, metrics)),
          ),
        ),
        finalize(() => this.metricsLoading.set(false)),
      )
      .subscribe({
        next: (metrics) => {
          this._metrics$.next(metrics);
        },
        error: (error) => {
          console.error(error);
        },
      });
  }

  parseLoadedMetrics(shop: Shop, metrics?: OrderEditMetricsDto): Record<string, OrderEditingMetric> {
    const resConfig = getOrderEditingMetricsConfig(OrderEditingService.isNewMetrics);

    if (!metrics) {
      return resConfig;
    }

    const impressions = metrics.impressions;
    const editsCompleted = metrics.editsCompleted;
    const engagementRate = impressions ? editsCompleted / impressions : 0;
    const shopCurrencySign = getSignOfCurrency(shop.metadata.currency);
    const engagementRateFormatted = this.decimalPipe.transform(engagementRate * 100, '1.0-1');
    const { suffix: impressionSuffix, value: impressionFormatted } = abbreviateLargeNumber(impressions);
    const { suffix: editsCompletedSuffix, value: editsCompletedFormatted } = abbreviateLargeNumber(editsCompleted);

    resConfig.impressions = {
      ...resConfig.impressions,
      value: impressionFormatted,
      suffix: impressionSuffix,
    };
    resConfig.editsCompleted = {
      ...resConfig.editsCompleted,
      value: editsCompletedFormatted,
      suffix: editsCompletedSuffix,
    };
    resConfig.editEngagementRate = {
      ...resConfig.editEngagementRate,
      value: engagementRateFormatted,
    };

    if (OrderEditingService.isNewMetrics) {
      // New metrics
      const editRevenue = metrics.editRevenue ?? 0;
      const netRevenue = metrics.netRevenue ?? 0;
      const editRevenueAbs = Math.abs(+editRevenue);
      const netRevenueAbs = Math.abs(+netRevenue);

      const { suffix: editRevenueSuffix, value: editRevenueFormatted } = abbreviateLargeNumber(editRevenueAbs);
      const { suffix: netRevenueSuffix, value: netRevenueFormatted } = abbreviateLargeNumber(netRevenueAbs);

      resConfig.editRevenue = {
        ...resConfig.editRevenue,
        value: editRevenueFormatted,
        suffix: editRevenueSuffix,
        prefix: `${editRevenue < 0 ? '-' : ''}${shopCurrencySign}`,
      };
      resConfig.netRevenue = {
        ...resConfig.netRevenue,
        value: netRevenueFormatted,
        suffix: netRevenueSuffix,
        prefix: `${netRevenue < 0 ? '-' : ''}${shopCurrencySign}`,
      };
    } else {
      // Old metrics
      const netRevenueChange = metrics.netRevenueChange ?? 0;
      const netRevenueChangeAbs = Math.abs(+netRevenueChange);
      const { suffix: netRevenueChangeSuffix, value: netRevenueChangeFormatted } =
        abbreviateLargeNumber(netRevenueChangeAbs);

      resConfig.netRevenueChange = {
        ...resConfig.netRevenueChange,
        value: netRevenueChangeFormatted,
        suffix: netRevenueChangeSuffix,
        prefix: `${netRevenueChange < 0 ? '-' : ''}${shopCurrencySign}`,
      };
    }

    return resConfig;
  }

  loadAddressCorrectionHistory$(params: AddressCorrectionHistoryQuery): Observable<AddressCorrectionHistoryResponse> {
    const queryParams: Record<string, string | number> = {};
    if (params.cursor) queryParams['cursor'] = params.cursor;
    if (params.first) queryParams['first'] = params.first;
    if (params.last) queryParams['last'] = params.last;
    if (params.startDate) queryParams['startDate'] = params.startDate;
    if (params.endDate) queryParams['endDate'] = params.endDate;
    if (params.orderNumber) queryParams['orderNumber'] = params.orderNumber;
    if (params.status) queryParams['status'] = params.status;

    return this.http.get<AddressCorrectionHistoryResponse>(
      `${OrderEditingService.API_URL}/order-edits/address-corrections`,
      {
        headers: { Authorization: `Bearer ${this.apiService.jwtToken}` },
        params: queryParams,
      },
    );
  }

  loadAddressCorrectionCount$(
    params: Omit<AddressCorrectionHistoryQuery, 'cursor' | 'first' | 'last'>,
  ): Observable<AddressCorrectionCountResponse> {
    const queryParams: Record<string, string | number> = {};
    if (params.startDate) queryParams['startDate'] = params.startDate;
    if (params.endDate) queryParams['endDate'] = params.endDate;
    if (params.orderNumber) queryParams['orderNumber'] = params.orderNumber;
    if (params.status) queryParams['status'] = params.status;

    return this.http.get<AddressCorrectionCountResponse>(
      `${OrderEditingService.API_URL}/order-edits/address-corrections/count`,
      {
        headers: { Authorization: `Bearer ${this.apiService.jwtToken}` },
        params: queryParams,
      },
    );
  }

  private loadConfig() {
    this.http
      .get<OrderEditing.Config>(`${OrderEditingService.API_URL}/order-editing-config`, {
        headers: {
          Authorization: `Bearer ${this.apiService.jwtToken}`,
        },
      })
      .subscribe({
        next: (config) => {
          this._loadedConfig$.next(config);
        },
        error: (error) => {
          console.error(error);
          this.toastService.showToast({ mode: 'danger', message: 'Failed to load order editing' });
        },
      });
  }

  private prepareConfigForSave(shop: Shop) {
    const formValue = this.form.value;
    const shopName = shop?.name;
    const shopCountryCode = shop?.address?.countryCode;
    const shopTimeZone = shop?.metadata?.ianaTimeZone;
    const shopCurrency = shop?.metadata?.currency;
    const config = {
      ...formValue,
      shopName,
      shopCountryCode,
      shopTimeZone,
      shopCurrency,
    } as any as OrderEditing.Config;
    return config;
  }

  saveConfig$(): Observable<OrderEditing.Config> {
    return this.shopService.shop$.pipe(
      filter((shop) => !!shop),
      take(1),
      map((shop) => this.prepareConfigForSave(shop)),
      switchMap((newConfig) => this.handleDisableConfirmationIfNeeded(newConfig as OrderEditing.Config)),
      switchMap(({ config, proceed, ordersInProgress }) => {
        if (!proceed) {
          // User cancelled the confirmation dialog
          return of(null);
        }
        const { apiFunction, url } = this.getConfigApiFunction(config);
        return this.saveConfigRequest(apiFunction, url, config).pipe(
          switchMap((savedConfig) => {
            if (ordersInProgress) {
              // Fire the close edits request, but always return the config
              return this.closeInEditWindowOrders$().pipe(map(() => savedConfig));
            }
            return of(savedConfig);
          }),
        );
      }),
      tap((config: OrderEditing.Config) => {
        if (config) {
          this._loadedConfig$.next(config);
        }
      }),
      filter((config): config is OrderEditing.Config => !!config), // filter out nulls
    );
  }

  private closeInEditWindowOrders$(): Observable<any> {
    return this.http
      .post(
        `${OrderEditingService.API_URL}/order-edits/close-in-edit-window`,
        {},
        {
          headers: {
            Authorization: `Bearer ${this.apiService.jwtToken}`,
          },
        },
      )
      .pipe(catchError(() => of(null)));
  }

  private getConfigApiFunction(config: OrderEditing.Config) {
    const id = this._loadedConfig$.getValue()?.id;
    const apiFunction = (id ? this.http.put : this.http.post).bind(this.http);
    const url = [OrderEditingService.API_URL, 'order-editing-config', id].filter(Boolean).join('/');
    return { apiFunction, url };
  }

  private shouldShowDisableConfirmation(newConfig: OrderEditing.Config): boolean {
    const prevState = this._loadedConfig$.getValue()?.state;
    return (
      newConfig.state === OrderEditing.State.DISABLED &&
      [OrderEditing.State.LIVE, OrderEditing.State.TEST].includes(prevState)
    );
  }

  private getInEditWindowCount(): Observable<number> {
    return this.http
      .get<{ count: number }>(`${OrderEditingService.API_URL}/order-edits/in-edit-window-count`, {
        headers: {
          Authorization: `Bearer ${this.apiService.jwtToken}`,
        },
      })
      .pipe(
        map((res) => res.count),
        catchError(() => of(0)),
      );
  }

  private showDisableConfirmationDialog(count: number): Observable<boolean> {
    return this.matDialog
      .open(ConfirmationDialogComponent, {
        data: {
          confirmText: 'Disable now',
          cancelText: 'Cancel',
          title: `Disable order editing`,
          description: `You currently have ${count} order${count > 1 ? 's' : ''} in the edit window.\nDisabling this feature will immediately end their edit period and capture their \npayments.\nAre you sure you want to proceed?`,
        },
      })
      .afterClosed();
  }

  private handleDisableConfirmationIfNeeded(
    newConfig: OrderEditing.Config,
  ): Observable<{ config: OrderEditing.Config; proceed: boolean; ordersInProgress: boolean }> {
    if (!this.shouldShowDisableConfirmation(newConfig)) {
      return of({ config: newConfig, proceed: true, ordersInProgress: false });
    }
    return this.getInEditWindowCount().pipe(
      switchMap((count) => {
        return this.showDisableConfirmationDialog(count).pipe(
          map((confirmed) => ({
            config: newConfig,
            proceed: !!confirmed,
            ordersInProgress: count > 0 && !!confirmed,
          })),
        );
      }),
    );
  }

  private saveConfigRequest(
    apiFunction: any,
    url: string,
    config: OrderEditing.Config,
  ): Observable<OrderEditing.Config> {
    return apiFunction(url, config, {
      headers: {
        Authorization: `Bearer ${this.apiService.jwtToken}`,
      },
    });
  }
}
